import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { MdOutlineEdit, MdMerge, MdBlock, MdCheck } from 'react-icons/md';
import { FiEye } from 'react-icons/fi';
import { FaPlus } from 'react-icons/fa6';
import CampaignActionDropdown from '~/components/CampaignRequestDetails/CampaignActionDropdown';
import IndeterminateCheckbox from '~/components/Checkboxes/IndeterminateCheckbox';
import { UserRow } from '~/components/Users/types/User';
import { useDebouncedURLSearchParams } from '~/hooks/useDebouncedUrlSearchParams';
import { fetchUsers, fetchRoles, fetchDepartments } from '~/services/users';
import { Pagination } from '~/components/Pagination';
import AddUserDrawer from '~/components/Users/drawers/AddUserDrawer';
import { startCase } from 'lodash';
import { FaSearch } from 'react-icons/fa';
import moment from 'moment';

const columnHelper = createColumnHelper<UserRow>();

const columns = [
    columnHelper.accessor('id', {
        id: 'select',
        header: ({ table }) => (
            <IndeterminateCheckbox
                {...{
                    name: '',
                    checked: table.getIsAllRowsSelected(),
                    indeterminate: table.getIsSomeRowsSelected(),
                    onChange: table.getToggleAllRowsSelectedHandler(),
                }}
            />
        ),
        cell: ({ row }) => (
            <div className='px-1'>
                <IndeterminateCheckbox
                    {...{
                        name: '',
                        checked: row.getIsSelected(),
                        disabled: !row.getCanSelect(),
                        indeterminate: row.getIsSomeSelected(),
                        onChange: row.getToggleSelectedHandler(),
                    }}
                />
            </div>
        ),
    }),
    columnHelper.accessor('person.first_name', {
        id: 'first_name',
        header: 'Name',
        cell: (props) => (
            <Link to={`/users/${props.row.original.id}`}>
                <div className='font-bold text-primary'>
                    {props.renderValue()}{' '}
                    {props.row.original?.person?.last_name}
                </div>
                <div className='text-sm text-secondary'>
                    {props.row.original?.person?.email}
                </div>
            </Link>
        ),
    }),
    columnHelper.accessor('roles', {
        header: 'Role',
        cell: (props) => {
            const roles = props.renderValue();
            return roles?.map((role) => {
                return <div key={role}>{startCase(role)}</div>;
            });
        },
    }),
    columnHelper.accessor('is_superuser', {
        id: 'access',
        header: 'Access',
        // The two elevated permissions live on the auth record, not in the
        // role list, so they were invisible from here (HHLTB-2676).
        cell: (props) => {
            const { is_superuser, is_staff } = props.row.original;
            if (!is_superuser && !is_staff) {
                return '';
            }
            return (
                <div className='flex flex-col gap-1'>
                    {is_superuser && (
                        <span className='w-fit whitespace-nowrap rounded-full bg-danger bg-opacity-10 px-2 py-0.5 text-xs font-medium text-danger'>
                            Super Admin
                        </span>
                    )}
                    {is_staff && (
                        <span className='w-fit whitespace-nowrap rounded-full bg-warning bg-opacity-10 px-2 py-0.5 text-xs font-medium text-black dark:text-white'>
                            Admin Site
                        </span>
                    )}
                </div>
            );
        },
    }),
    columnHelper.accessor('departments', {
        header: 'Department',
        cell: (props) => {
            const departments = props.renderValue();
            return departments?.map((department) => (
                <div key={`${department.name}-${department.role}`}>
                    {department.name} {department.role}
                </div>
            ));
        },
    }),
    columnHelper.accessor('is_active', {
        header: 'Active',
        cell: (info) =>
            info.getValue() === true ? (
                <MdCheck className='text-primary' size={20} />
            ) : (
                ''
            ),
    }),
    columnHelper.accessor('last_login', {
        id: 'lastActive',
        header: 'Last Active',
        cell: (props) => {
            const loginDate = moment(props.row.original.last_login);
            if (!props.row.original.last_login || loginDate.year() <= 1970) {
                return '';
            }
            return loginDate.format('MM/DD/YYYY');
        },
    }),
];

const Users: React.FC = () => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [data, setData] = React.useState([]);
    const [pages, setPages] = React.useState<number>(1);
    const [roles, setRoles] = React.useState<{ name: string; id: number }[]>(
        []
    );
    const [departments, setDepartments] = React.useState<
        { name: string; id: number }[]
    >([]);
    const [filters, setFilters, results, isLoading] =
        useDebouncedURLSearchParams(
            {
                page: 1,
                role_id: null,
                department_id: null,
                status: '',
                name: '',
                // A string, not a boolean: these filters round-trip through
                // the query string, which reads back as text either way.
                staff_only: '',
                super_admin: '',
                admin_site: '',
            },
            'all-users',
            fetchUsers,
            [{ key: 'name', debounceTime: 500 }]
        );

    useEffect(() => {
        if (results) {
            const dataFetched = results;
            setData(dataFetched.results);
            setPages(Math.ceil(dataFetched.count / 20));
        }
    }, [results, isLoading]);

    const getAndSetRoles = async () => {
        const rolesData = await fetchRoles();

        setRoles(rolesData);
    };
    const getAndSetDepartments = async () => {
        try {
            setDepartments(await fetchDepartments());
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    };
    useEffect(() => {
        getAndSetRoles();
        getAndSetDepartments();
    }, []);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleFilterChange = (
        key: keyof typeof filters,
        value: string | number
    ) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
            ...(key !== 'page' ? { page: 1 } : {}),
        }));
    };

    return (
        <>
            <Breadcrumb pageName='Users' />
            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark' style={{overflow: 'visible'}}>
                <div className='flex items-center justify-between gap-4 overflow-x-auto px-8 pb-4'>
                    <div className='flex gap-4'>
                        <div className='relative w-full min-w-[125px] max-w-[175px] flex-1'>
                            <input
                                name='user-search'
                                type='search'
                                value={filters.name}
                                onChange={(e) =>
                                    handleFilterChange('name', e.target.value)
                                }
                                placeholder='Search users...'
                                autoComplete='off'
                                data-1p-ignore
                                data-lpignore='true'
                                className='w-full rounded border border-stroke p-[6px] px-3 py-2 pr-10 outline-none focus:border-primary'
                            />
                            <FaSearch
                                color='gray'
                                className='absolute right-3 top-1/2 -translate-y-1/2'
                            />
                        </div>
                        <div className='relative w-full min-w-[125px] max-w-[175px] flex-1'>
                            <select
                                name='role'
                                id='role'
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                                onChange={(val) => {
                                    const value = val?.target?.value;
                                    handleFilterChange(
                                        'role_id',
                                        value ? parseInt(value) : ''
                                    );
                                }}
                                {...(filters.role_id
                                    ? { value: filters.role_id }
                                    : null)}
                            >
                                <option value=''>Role</option>
                                {roles.map((role, i) => (
                                    <option value={role.id} key={i}>
                                        {startCase(role.name)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='relative w-full min-w-[125px] max-w-[175px] flex-1'>
                            <select
                                name='department'
                                id='department'
                                className='w-full flex-1 rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                                onChange={(e) => {
                                    const value = e?.target?.value;
                                    handleFilterChange(
                                        'department_id',
                                        value ? parseInt(value) : ''
                                    );
                                }}
                                {...(filters.department_id
                                    ? { value: filters.department_id }
                                    : null)}
                            >
                                <option value=''>Department</option>
                                {departments.map((department) => (
                                    <option
                                        key={department.id}
                                        value={department.id}
                                    >
                                        {department.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='relative w-full min-w-[125px] max-w-[175px] flex-1'>
                            <select
                                name='status'
                                id='status'
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                                value={filters.status}
                                onChange={(e) =>
                                    handleFilterChange('status', e.target.value)
                                }
                            >
                                <option value=''>Status</option>
                                <option value='active'>Active</option>
                                <option value='inactive'>Inactive</option>
                            </select>
                        </div>
                        <div className='flex flex-wrap items-center gap-x-4 gap-y-1'>
                            {/*
                                Replaces the old My Staff page, which listed every
                                user including clients (HHLTB-2665).
                            */}
                            <label
                                htmlFor='staff_only'
                                className='flex shrink-0 cursor-pointer select-none items-center gap-2 whitespace-nowrap text-sm'
                            >
                                <input
                                    type='checkbox'
                                    name='staff_only'
                                    id='staff_only'
                                    className='h-4 w-4 cursor-pointer accent-primary'
                                    checked={filters.staff_only === 'true'}
                                    onChange={(e) =>
                                        handleFilterChange(
                                            'staff_only',
                                            e.target.checked ? 'true' : ''
                                        )
                                    }
                                />
                                Staff only
                            </label>
                            {/*
                                The two elevated permissions, so there is a list
                                that can be trusted for a periodic access review
                                (HHLTB-2676). They are separate: Super Admin is
                                what allows opening any client's portal, admin
                                site access is only the old Django admin.
                            */}
                            <label
                                htmlFor='super_admin'
                                className='flex shrink-0 cursor-pointer select-none items-center gap-2 whitespace-nowrap text-sm'
                            >
                                <input
                                    type='checkbox'
                                    name='super_admin'
                                    id='super_admin'
                                    className='h-4 w-4 cursor-pointer accent-primary'
                                    checked={filters.super_admin === 'true'}
                                    onChange={(e) =>
                                        handleFilterChange(
                                            'super_admin',
                                            e.target.checked ? 'true' : ''
                                        )
                                    }
                                />
                                Super Admin
                            </label>
                            <label
                                htmlFor='admin_site'
                                className='flex shrink-0 cursor-pointer select-none items-center gap-2 whitespace-nowrap text-sm'
                            >
                                <input
                                    type='checkbox'
                                    name='admin_site'
                                    id='admin_site'
                                    className='h-4 w-4 cursor-pointer accent-primary'
                                    checked={filters.admin_site === 'true'}
                                    onChange={(e) =>
                                        handleFilterChange(
                                            'admin_site',
                                            e.target.checked ? 'true' : ''
                                        )
                                    }
                                />
                                Admin site access
                            </label>
                        </div>
                    </div>

                    <div className='flex gap-4'>
                        <button
                            className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full bg-warning px-3 py-2 text-center text-sm font-bold uppercase hover:bg-opacity-90 lg:px-5'
                            onClick={() => setIsOpen(true)}
                        >
                            <FaPlus />
                            Add User
                        </button>
                    </div>
                </div>

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>Showing {Math.min(20, (results?.count || 0) - (filters.page - 1) * 20)} of {results?.count || 0} results</p>

                    <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <FiEye className='text-primary' size={18} />
                            View
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlineEdit className='text-primary' size={18} />
                            Edit
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdMerge className='text-primary' size={18} />
                            Merge
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdBlock className='text-red' size={18} />
                            Close
                        </div>
                    </div>
                </div>

                <div className='overflow-x-auto overflow-y-visible'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map(
                                        (header, index) => (
                                            <th
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                    ...(index === 0
                                                        ? { width: '70px' }
                                                        : null),
                                                }}
                                                key={header.id}
                                            >
                                                <div className='flex items-center'>
                                                    <span
                                                        style={{
                                                            ...(index === 0
                                                                ? {
                                                                    height: '34px',
                                                                }
                                                                : null),
                                                        }}
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                header.column
                                                                    .columnDef
                                                                    .header,
                                                                header.getContext()
                                                            )}
                                                    </span>
                                                    <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                                                        <span className='inline-block'>
                                                            <svg
                                                                xmlns='http://www.w3.org/2000/svg'
                                                                width='17'
                                                                height='16'
                                                                viewBox='0 0 17 16'
                                                                fill='none'
                                                            >
                                                                <path
                                                                    d='M8.5 14.6666L4.5 10.6666L5.45 9.71659L8.5 12.7666L11.55 9.71659L12.5 10.6666L8.5 14.6666ZM5.46667 6.26659L4.5 5.33325L8.5 1.33325L12.5 5.33325L11.5333 6.26659L8.5 3.23325L5.46667 6.26659Z'
                                                                    fill='#131212'
                                                                />
                                                            </svg>
                                                        </span>
                                                    </div>
                                                </div>
                                            </th>
                                        )
                                    )}
                                    <th
                                        style={{
                                            width: '70px',
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                        }}
                                    ></th>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                    <tr key={index}>
                                        {columns.map((_column, colIndex) => (
                                            <td
                                                key={colIndex}
                                                className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                            >
                                                <div className='max-w-sm animate-pulse'>
                                                    <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                </div>
                                            </td>
                                        ))}
                                        <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                            <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                                : table.getRowModel().rows.map((row) => (
                                    <tr
                                        className='border-t border-stroke'
                                        key={row.id}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                                key={cell.id}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef
                                                        .cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        ))}
                                        <td
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                        >
                                            <CampaignActionDropdown />
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={filters.page}
                    pages={pages}
                    setPage={(val: number) => handleFilterChange('page', val)}
                    totalCount={results?.count}
                />
            </section>
            <AddUserDrawer isOpen={isOpen} setIsOpen={setIsOpen} />
        </>
    );
};

export default Users;
